import { createMultiplayerSimulation } from '../multiplayer/grid'
import type { RacerTelemetry, RoomPlayer } from '../multiplayer/protocol'
import type { RuntimeRaceSnapshot } from '../multiplayer/race-snapshot'
import { applyDriverHit } from './combat'
import { synchronizeRaceStart } from './race'
import { createSimulation, stepSimulation } from './simulation'
import type { ControlInput, SimulationEvent, SimulationState } from './types'
import { EMPTY_INPUT } from './vehicle'

const FIXED_STEP = 1 / 60
const MAX_VIRTUAL_FRAMES = 36_000
const MAX_ACCUMULATED_TIME = 0.25
const PUBLISH_INTERVAL = 0.08

export type RuntimeListener = () => void
export type RuntimeEventListener = (events: readonly SimulationEvent[], state: SimulationState) => void
export type RuntimeController = (state: SimulationState) => ControlInput

export class GameRuntime {
  private state: SimulationState = createSimulation()
  private input: ControlInput = { ...EMPTY_INPUT }
  private multiplayerStartsAt: number | null = null
  private officialRaceVersion: { raceId: string; revision: number } | null = null
  private paused = false
  private virtualBatchActive = false
  private accumulator = 0
  private publishAccumulator = 0
  private readonly listeners = new Set<RuntimeListener>()
  private readonly eventListeners = new Set<RuntimeEventListener>()

  constructor(private readonly now: () => number = Date.now) {}

  getState = (): SimulationState => this.state

  getInput(): ControlInput {
    return { ...this.input }
  }

  isPaused(): boolean {
    return this.paused
  }

  setInput(next: Partial<ControlInput>): void {
    this.input = { ...this.input, ...next }
  }

  clearInput(): void {
    this.input = { ...EMPTY_INPUT }
  }

  pause(): void {
    this.assertVirtualBatchInactive('pause')
    this.paused = true
    this.clearInput()
    this.publish()
  }

  resume(): void {
    this.assertVirtualBatchInactive('resume')
    this.paused = false
    this.publish()
  }

  restart(): void {
    this.assertVirtualBatchInactive('restart')
    this.state = createSimulation()
    this.multiplayerStartsAt = null
    this.officialRaceVersion = null
    this.accumulator = 0
    this.publishAccumulator = 0
    this.paused = false
    this.clearInput()
    this.publish()
  }

  configureMultiplayer(players: RoomPlayer[], localClientId: string): void {
    this.assertVirtualBatchInactive('configureMultiplayer')
    this.state = createMultiplayerSimulation(players, localClientId)
    this.multiplayerStartsAt = null
    this.officialRaceVersion = null
    this.accumulator = 0
    this.publishAccumulator = 0
    this.paused = true
    this.clearInput()
    this.publish()
  }

  activateMultiplayerRace(startsAt: number): void {
    this.assertVirtualBatchInactive('activateMultiplayerRace')
    const race = synchronizeRaceStart(this.state.race, startsAt, this.now())
    this.multiplayerStartsAt = startsAt
    this.state = { ...this.state, race }
    this.accumulator = 0
    this.publishAccumulator = 0
    this.paused = false
    this.clearInput()
    this.publish()
  }

  applyRemoteTelemetry(
    vehicleId: string,
    telemetry: Omit<RacerTelemetry, 'type' | 'sequence' | 'sentAt' | 'clientId'>,
  ): void {
    const vehicle = this.state.vehicles.find((candidate) => candidate.id === vehicleId)
    if (vehicle?.kind !== 'remote') return
    const vehicles = this.state.vehicles.map((candidate) =>
      candidate.id === vehicleId
        ? {
            ...candidate,
            position: { ...telemetry.position },
            velocity: { ...telemetry.velocity },
            heading: telemetry.heading,
            speed: telemetry.speed,
            steer: telemetry.steer,
            boost: telemetry.boost,
            boostActive: telemetry.boostActive,
            drift: { ...candidate.drift, active: telemetry.driftActive, angle: telemetry.driftAngle },
            ammo: telemetry.ammo,
            weaponCooldown: telemetry.weaponCooldown ?? candidate.weaponCooldown,
            reloadTimer: telemetry.reloadTimer,
            driverHitCount: telemetry.driverHitCount,
            driverStunTimer: telemetry.driverStunTimer,
            engineFatigue: telemetry.engineFatigue,
            offTrack: telemetry.offTrack,
          }
        : candidate,
    )
    this.state = { ...this.state, vehicles }
    this.publish()
  }

  applyOfficialRaceSnapshot(snapshot: RuntimeRaceSnapshot): boolean {
    if (this.state.classificationAuthority !== 'server' || !isCompleteOfficialSnapshot(snapshot, this.state)) {
      return false
    }
    if (
      this.officialRaceVersion &&
      (snapshot.raceId !== this.officialRaceVersion.raceId || snapshot.revision <= this.officialRaceVersion.revision)
    ) {
      return false
    }
    const now = this.now()
    const elapsed = Math.max(0, (now - snapshot.startsAt) / 1000)
    const finishCountdown =
      snapshot.phase === 'finish-countdown' && snapshot.finishDeadlineAt !== null
        ? Math.max(0, (snapshot.finishDeadlineAt - now) / 1000)
        : null
    const progress = Object.fromEntries(
      this.state.race.vehicleIds.map((vehicleId) => {
        const official = snapshot.progress[vehicleId]
        const current = this.state.race.progress[vehicleId]
        if (!official || !current) throw new Error('Validated official progress is missing')
        return [
          vehicleId,
          {
            ...current,
            lap: official.lap,
            lastCheckpoint: official.lastCheckpoint,
            nextCheckpoint: official.nextCheckpoint,
            finishedAt: official.finishedAt,
            recordStatus: official.recordStatus,
          },
        ]
      }),
    )
    this.state = {
      ...this.state,
      race: {
        ...this.state.race,
        phase: snapshot.phase,
        countdown: snapshot.phase === 'countdown' ? Math.max(0, (snapshot.startsAt - now) / 1000) : 0,
        elapsed: snapshot.phase === 'countdown' ? 0 : elapsed,
        progress,
        finishOrder: [...snapshot.finishOrder],
        finishCountdown,
      },
      ranking: [...snapshot.ranking],
    }
    this.multiplayerStartsAt = snapshot.startsAt
    this.officialRaceVersion = { raceId: snapshot.raceId, revision: snapshot.revision }
    this.publish()
    return true
  }

  applyNetworkHit(): void {
    const playerId = this.state.race.playerId
    this.state = {
      ...this.state,
      vehicles: this.state.vehicles.map((vehicle) =>
        vehicle.id === playerId ? applyDriverHit(vehicle).vehicle : vehicle,
      ),
    }
    this.publish()
  }

  stepFrame(deltaTime: number): void {
    this.assertVirtualBatchInactive('stepFrame')
    if (this.paused) return
    const safeDelta = Math.max(0, Math.min(deltaTime, MAX_ACCUMULATED_TIME))
    this.accumulator += safeDelta
    this.publishAccumulator += safeDelta
    const frameEvents: SimulationState['events'] = []
    let stepped = false
    while (this.accumulator + 1e-9 >= FIXED_STEP) {
      const nextState = stepSimulation(this.state, this.input, FIXED_STEP)
      frameEvents.push(...nextState.events)
      this.state = nextState
      this.accumulator -= FIXED_STEP
      stepped = true
    }
    this.reconcileMultiplayerRace()
    if (stepped) {
      this.state = { ...this.state, events: frameEvents }
      this.publishEvents(frameEvents)
    }
    if (this.publishAccumulator >= PUBLISH_INTERVAL) {
      this.publishAccumulator %= PUBLISH_INTERVAL
      this.publish()
    }
  }

  runVirtualFrames(frameCount: number, controller: RuntimeController): void {
    this.assertVirtualBatchInactive('runVirtualFrames')
    if (!Number.isFinite(frameCount) || !Number.isInteger(frameCount) || frameCount < 0) {
      throw new RangeError('frameCount must be a non-negative finite integer')
    }
    if (frameCount === 0 || this.paused) return

    const batchSize = Math.min(frameCount, MAX_VIRTUAL_FRAMES)
    let nextState = this.state
    const batchEvents: SimulationEvent[] = []
    this.virtualBatchActive = true
    try {
      for (let frame = 0; frame < batchSize; frame += 1) {
        const input = controller(nextState)
        nextState = stepSimulation(nextState, input, FIXED_STEP)
        batchEvents.push(...nextState.events)
      }
      this.state = { ...nextState, events: batchEvents }
      this.accumulator = 0
      this.publishAccumulator = 0
    } finally {
      this.virtualBatchActive = false
    }
    this.publishEvents(batchEvents)
    this.publish()
  }

  subscribe = (listener: RuntimeListener): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  subscribeEvents = (listener: RuntimeEventListener): (() => void) => {
    this.eventListeners.add(listener)
    return () => this.eventListeners.delete(listener)
  }

  private publish(): void {
    for (const listener of this.listeners) listener()
  }

  private publishEvents(events: readonly SimulationEvent[]): void {
    if (events.length === 0) return
    for (const listener of this.eventListeners) listener(events, this.state)
  }

  private reconcileMultiplayerRace(): void {
    if (this.multiplayerStartsAt === null) return
    const race = this.state.race
    if (race.phase === 'finish-countdown' || race.phase === 'finished') return
    const synchronized = synchronizeRaceStart(race, this.multiplayerStartsAt, this.now())
    if (race.phase === 'racing') {
      if (synchronized.phase !== 'racing' || synchronized.elapsed <= race.elapsed) return
      this.state = { ...this.state, race: { ...race, countdown: 0, elapsed: synchronized.elapsed } }
      return
    }
    if (synchronized.phase === 'countdown') {
      if (synchronized.countdown >= race.countdown) return
      this.state = { ...this.state, race: { ...race, countdown: synchronized.countdown, elapsed: 0 } }
      return
    }
    this.state = {
      ...this.state,
      race: { ...race, phase: 'racing', countdown: 0, elapsed: Math.max(race.elapsed, synchronized.elapsed) },
    }
  }

  private assertVirtualBatchInactive(operation: string): void {
    if (this.virtualBatchActive) {
      throw new Error(`Cannot ${operation} while a virtual frame batch is active`)
    }
  }
}

function isCompleteOfficialSnapshot(snapshot: RuntimeRaceSnapshot, state: SimulationState): boolean {
  if (
    snapshot.raceId.length === 0 ||
    !Number.isSafeInteger(snapshot.revision) ||
    snapshot.revision < 0 ||
    !Number.isFinite(snapshot.startsAt) ||
    (snapshot.phase === 'finish-countdown') !== (snapshot.finishDeadlineAt !== null) ||
    (snapshot.finishDeadlineAt !== null && !Number.isFinite(snapshot.finishDeadlineAt))
  ) {
    return false
  }
  const expected = new Set(state.race.vehicleIds)
  const progressIds = Object.keys(snapshot.progress)
  if (
    expected.size === 0 ||
    progressIds.length !== expected.size ||
    !progressIds.every(
      (vehicleId) => expected.has(vehicleId) && snapshot.progress[vehicleId]?.vehicleId === vehicleId,
    ) ||
    !isExactIdList(snapshot.ranking, expected)
  ) {
    return false
  }
  const finishIds = new Set(snapshot.finishOrder)
  return (
    finishIds.size === snapshot.finishOrder.length &&
    snapshot.finishOrder.every((vehicleId, index) => expected.has(vehicleId) && snapshot.ranking[index] === vehicleId)
  )
}

function isExactIdList(vehicleIds: string[], expected: Set<string>): boolean {
  return (
    vehicleIds.length === expected.size &&
    new Set(vehicleIds).size === vehicleIds.length &&
    vehicleIds.every((vehicleId) => expected.has(vehicleId))
  )
}
